import React, { useEffect, useState } from "react";
import { AutoSizer, Column, SortDirection, Table } from "react-virtualized";
import { getNumericComparator, getStringComparator, reverseOrder } from "@/utils/collect";
import styles from "@/components/LanguageModelArtifact/style.module.css";
import { FEEDBACK_BREAKPOINTS } from "@/containers/LanguageModelArtifact/reducer";
import { ProgressBar } from "@/components/LanguageModelArtifact/ProgressBar";
import { Button } from "@/aible";
import GearIconPurple from "images/gear-purple.svg";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/aible/DropdownMenu/DropdownMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { isEnabled } from "@/utils/featureFlags";
const TrainingDataTable = ({ data, searchFilter, downloadArtifactFeedback, updateShowModal, updateSelectedDataset, updateSelectedModel, handleDelete, handleModelEvaluationAction, updateShowExampleSetModal, updateSelectedExampleSetId }) => {
    const [sortBy, updateSortBy] = useState("createdDate");
    const [sortDirection, updateSortDirection] = useState(SortDirection.DESC);
    const [sortedData, updateSortedData] = useState(data);
    const [isLoading, updateIsLoading] = useState(false);
    const columns = [
        { label: "Name", dataKey: "name", width: 300, flexGrow: 1 },
        { label: "Status", dataKey: "status", width: 250 },
        { label: "Total Feedback", dataKey: "totalFeedback", width: 150 },
        { label: "Progress Towards Recommended Amount", dataKey: "totalProgress", width: 340, flexGrow: 1 },
        { label: "Owner", dataKey: "owner", width: 180 },
        { label: "Date Created", dataKey: "createdDate", width: 200 },
        { label: "", dataKey: "actions", width: 280 },
        { label: "", dataKey: "more", width: 10 }
        // { label: "", dataKey: "id", width: 100 },
    ];
    const getSortFunction = (sortBy) => {
        switch (sortBy) {
            case "createdDate":
                return getNumericComparator((x) => Date.parse(x[sortBy]));
            case "status":
            case "owner":
            case "label":
                return getStringComparator((x) => x[sortBy]);
            case "totalFeedback":
            case "totalProgress":
            case "id":
            case "value":
                return getNumericComparator((x) => x[sortBy]);
            default:
                return getNumericComparator(() => 0);
        }
    };
    const sort = ({ sortBy, sortDirection }) => {
        const sortFunc = getSortFunction(sortBy);
        updateSortBy(sortBy);
        updateSortDirection(sortDirection);
        return updateSortedData(data.toSorted(sortDirection === SortDirection.ASC ? sortFunc : reverseOrder(sortFunc)));
    };
    useEffect(() => {
        sort({ sortBy, sortDirection });
    }, [data]);
    const filteredData = sortedData.filter((item) => searchFilter.length === 0 ? item : item?.name?.toLowerCase()?.includes(searchFilter?.toLowerCase()));
    const handleFineTuneAction = (dataset) => {
        updateSelectedDataset(dataset);
        updateSelectedModel(undefined);
        updateShowModal(true);
    };
    const handleDeleteAction = (modelId) => {
        updateIsLoading(true);
        handleDelete(modelId);
        updateIsLoading(false);
    };
    const rowHeight = 44;
    const tableHeight = filteredData.length * rowHeight + 30;
    if ((searchFilter.length ?? 0) > 0 && filteredData.length < 1) {
        return React.createElement("div", { className: styles.noMatchingText }, "No matching datasets found");
    }
    return (React.createElement("div", { className: styles.dataTableWrapper },
        React.createElement(AutoSizer, null, ({ width }) => (React.createElement(Table, { width: width, height: tableHeight, headerHeight: 30, rowHeight: rowHeight, rowCount: filteredData.length, rowGetter: ({ index }) => filteredData[index], sort: sort, sortBy: sortBy, sortDirection: sortDirection, rowClassName: styles.fineTuningRow, className: styles.fineTuningTable, headerClassName: styles.fineTuningHeaderRow, onRowClick: ({ rowData }) => {
                const exampleSetId = rowData?.exampleSet?.id;
                if (exampleSetId && isEnabled("edit-example-sets")) {
                    updateSelectedExampleSetId(exampleSetId);
                    updateShowExampleSetModal(true);
                }
            } }, columns.map((column, index) => {
            if (column.dataKey === "label") {
                return;
            }
            if (column.dataKey === "status") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, flexGrow: column.flexGrow, className: styles.fineTuningItem }));
            }
            if (column.dataKey === "totalProgress") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, flexGrow: column.flexGrow, className: styles.fineTuningItem, cellRenderer: ({ cellData }) => (React.createElement("div", { className: styles.progressCell },
                        React.createElement("div", { className: styles.progressText }, `${Math.min((cellData / FEEDBACK_BREAKPOINTS.RECOMMENDED) * 100, 100).toFixed(2)}%`),
                        React.createElement(ProgressBar, { value: (cellData / FEEDBACK_BREAKPOINTS.RECOMMENDED) * 100 }))) }));
            }
            if (column.dataKey === "id") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, cellRenderer: ({ rowData }) => (React.createElement(Button, { isSmall: true, onClick: (e) => {
                            e.stopPropagation();
                            downloadArtifactFeedback(rowData);
                        }, style: { padding: "5px 7px" } },
                        React.createElement("img", { src: GearIconPurple }))), className: styles.fineTuningSettings }));
            }
            if (column.dataKey === "actions") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningSettings, cellRenderer: ({ rowData }) => (React.createElement("div", { className: styles.fineTuningItemActions },
                        React.createElement(Button, { isSmall: true, onClick: (e) => {
                                e.stopPropagation();
                                handleModelEvaluationAction(rowData.id);
                            }, style: { padding: "5px 7px", marginRight: "4px" } }, "Evaluate Model"),
                        React.createElement(Button, { isSmall: true, onClick: (e) => {
                                e.stopPropagation();
                                handleFineTuneAction(rowData.exampleSet);
                            }, disabled: rowData.totalFeedback < FEEDBACK_BREAKPOINTS.MIN, style: { padding: "5px 7px", marginRight: "4px" } }, "Post Train New Model"))) }));
            }
            if (column.dataKey === "more") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningSettings, cellRenderer: ({ rowData }) => {
                        return (React.createElement("div", { className: styles.fineTuningItemMore },
                            React.createElement(DropdownMenu, null,
                                React.createElement(DropdownMenuTrigger, { asChild: true },
                                    React.createElement(FontAwesomeIcon, { icon: faEllipsisVertical })),
                                React.createElement(DropdownMenuContent, { className: "w-32", align: "end" },
                                    React.createElement(DropdownMenuItem, { onClick: () => {
                                            handleDeleteAction(rowData?.id);
                                        }, disabled: isLoading, className: "text-red-500" }, "Delete Dataset")))));
                    } }));
            }
            return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, flexGrow: column.flexGrow, className: styles.fineTuningItem }));
        }))))));
};
export default TrainingDataTable;
